'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { PluginInfo, PluginDetail } from "../types";
import { acceptedPluginUpdates, acceptedUpdate } from './system';

/** Toggle a plugin on/off. Optimistic: the installed list AND the open detail flip instantly so the UI
 *  reacts immediately, without waiting for the daemon's hot-reload + refetch. On settle we re-fetch the
 *  list, the detail and its logs (health derives from the log ring) so everything reflects the real
 *  backend state; on error the optimistic change rolls back. */
export function useTogglePlugin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; enabled: boolean; acknowledgeGrants?: string[] }) =>
      elowenClient.togglePlugin(v.name, v.enabled, v.acknowledgeGrants),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['plugins'] });
      const prevList = qc.getQueryData<PluginInfo[]>(['plugins']);
      const prevDetail = qc.getQueryData<PluginDetail>(['plugin', v.name]);
      qc.setQueryData<PluginInfo[]>(['plugins'], (cur) => cur?.map((p) => (p.name === v.name ? { ...p, enabled: v.enabled } : p)));
      qc.setQueryData<PluginDetail>(['plugin', v.name], (cur) => (cur ? { ...cur, enabled: v.enabled } : cur));
      return { prevList, prevDetail };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prevList) qc.setQueryData(['plugins'], ctx.prevList);
      if (ctx?.prevDetail) qc.setQueryData(['plugin', v.name], ctx.prevDetail);
    },
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: ['plugin', v.name] });
      void qc.invalidateQueries({ queryKey: ['plugin-logs', v.name] });
      // A toggled plugin adds/removes its slash commands — re-pull the menu's single source of truth.
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainCommands });
      // …and its browser UI (sidebar world + /p/<name> pages) appears/disappears with it.
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.pluginUi });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.platformMetadata });
    },
  });
}

/** Refresh both the marketplace catalog and the installed list after any install/update/uninstall/restore. */
function invalidatePluginViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['marketplace'] });
  void qc.invalidateQueries({ queryKey: ['plugins'] });
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainCommands });
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.pluginUi });
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.platformMetadata });
  // Installing, updating, removing or restoring a plugin can be the cron plugin itself, which decides
  // whether the scheduled-job branches exist at all — a stale `unavailable` would hide every branch.
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
}

/** Queue the approved registry candidate, without claiming it has been installed. */
export function useInstallPlugin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; enable?: boolean; withCore?: boolean }) => elowenClient.installPlugin(v.name, v.enable ?? true, v.withCore),
    onSuccess: (result, v) => acceptedUpdate(qc, result.requestId, { name: v.name, op: 'install', enable: v.enable ?? true }),
  });
}

/** Update an installed user plugin to the registry's newer version. A version that brings a power the
 *  operator has not agreed to is refused by the daemon until `acknowledgeGrants` names it — the same
 *  question an enable asks, owned by the shared consent hook. */
export function useUpdatePlugin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; withCore?: boolean }) => elowenClient.updatePlugin(v.name, v.withCore),
    onSuccess: (result, v) => acceptedUpdate(qc, result.requestId, { name: v.name, op: 'update',
      enable: qc.getQueryData<PluginInfo[]>(['plugins'])?.find(p => p.name === v.name)?.enabled ?? false }),

  });
}

export function useUpdateAllPlugins() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (withCore: boolean = false) => elowenClient.updateAllPlugins(withCore),
    onSuccess: result => acceptedPluginUpdates(qc, result.plugins),
  });
}

export function useRepairPlugin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => elowenClient.repairPlugin(name),
    onSuccess: (result, name) => acceptedUpdate(qc, result.requestId, { name, op: 'repair',
      enable: qc.getQueryData<PluginInfo[]>(['plugins'])?.find(p => p.name === name)?.enabled ?? false }),
  });
}

/** Remove a plugin — a user plugin is uninstalled (files deleted); a bundled plugin is soft-removed. */
export function useUninstallPlugin() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (name: string) => elowenClient.uninstallPlugin(name), onSuccess: () => invalidatePluginViews(qc) });
}

/** Restore a soft-removed bundled plugin (reappears disabled in the installed list). */
export function useRestorePlugin() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (name: string) => elowenClient.restorePlugin(name), onSuccess: () => invalidatePluginViews(qc) });
}

export function useSavePluginConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; values: Record<string, unknown>; expectedRevision?: number }) => elowenClient.savePluginConfig(v.name, v.values, v.expectedRevision),
    onSuccess: (response, v) => {
      if (response.config && response.secretsSet && response.revision !== undefined) {
        qc.setQueryData<PluginDetail>(['plugin', v.name], (current) => current ? {
          ...current, config: response.config!, secretsSet: response.secretsSet!, secretsUnreadable: response.secretsUnreadable, revision: response.revision,
        } : current);
      }
      void qc.invalidateQueries({ queryKey: ['plugins'] });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainCommands });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.platformMetadata });
    },
  });
}

/** Destructive — wipe the contents of a plugin's data directory. Refreshes that plugin's detail (data summary). */
export function useClearPluginData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => elowenClient.clearPluginData(name),
    onSuccess: (_r, name) => { void qc.invalidateQueries({ queryKey: ['plugin', name] }); },
  });
}
